import { Fragment, useEffect } from "react";
import { motion, Variants } from "framer-motion";
import { Background } from "../../../shared/ui/background";
import { NavBar } from "../../../shared/ui/navbar";
import { Title } from "../../../shared/ui/title";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectUser,
  updateUserStreak,
} from "../../../auth/data-access/store/authSlice";
import {
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import { checkTranslationKey } from "../../../utils/translation";
import { StreakMilestone } from "../../../shared/interfaces/streak.types";
import { StreakBanner } from "../../ui/streak-banner";
import { useGetStreakQuery } from "../../data-access/store/services/streak.service";
import "./streak-page.scss";

const milestoneVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: (index: number) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.3, delay: 0.05 * index },
  }),
};

/**
 * The standalone streak page (FR-27 to FR-30): the current count in a banner, a status line, and
 * the full milestone ladder in ascending order.
 */
export function StreakPage() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const translations = useAppSelector(selectTranslations);
  const lang = useAppSelector(selectLanguage);
  const streak = user?.character?.streak;

  // Renders immediately from whatever `/user/info` last returned, then corrects itself. Skipped
  // when there is no streak on the payload at all: the endpoint is a 404 with the flag off.
  const { data: freshStreak } = useGetStreakQuery(undefined, {
    skip: !streak,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (freshStreak) {
      dispatch(updateUserStreak(freshStreak));
    }
  }, [freshStreak, dispatch]);

  const milestones = streak?.milestones ?? [];
  const finalMilestone = milestones[milestones.length - 1]?.day;

  return (
    <div id="streak">
      <Background type="home">
        <NavBar hasAvatar></NavBar>
        <Title>
          {checkTranslationKey(translations[lang]?.Streak, "Streak")}
        </Title>

        <div className="streak-page-content">
          <StreakBanner days={streak?.current ?? 0} />

          <div className="streak-status">
            {streak?.completed ? (
              <>
                <p>
                  {finalMilestone}
                  {checkTranslationKey(
                    translations[lang]?.Day_streak_complete,
                    "-day streak complete!"
                  )}
                </p>
                <p>
                  {checkTranslationKey(
                    translations[lang]?.Iron_habit_badge_unlocked,
                    "Iron Habit badge unlocked!"
                  )}
                </p>
              </>
            ) : streak?.next_milestone ? (
              <>
                <p>
                  {checkTranslationKey(
                    translations[lang]?.Youre_on_fire,
                    "You're on fire!"
                  )}
                </p>
                <p>
                  {streak.next_milestone.days_remaining}{" "}
                  {checkTranslationKey(
                    translations[lang]?.Days_until_next_reward,
                    "days until your next reward."
                  )}
                </p>
              </>
            ) : (
              <p>
                {checkTranslationKey(
                  translations[lang]?.Start_your_streak,
                  "Play a game today to start your streak!"
                )}
              </p>
            )}
          </div>

          <div className="streak-milestones">
            {milestones.map((milestone, index) => (
              <Fragment key={milestone.day}>
                {index > 0 && (
                  <div
                    className={`milestone-connector${
                      milestones[index - 1].status === "achieved" &&
                      milestone.status === "achieved"
                        ? " milestone-connector--achieved"
                        : ""
                    }`}
                  />
                )}
                <motion.div
                  className="milestone"
                  variants={milestoneVariants}
                  initial="hidden"
                  animate="visible"
                  custom={index}
                >
                  <MilestoneRow milestone={milestone} />
                </motion.div>
              </Fragment>
            ))}
          </div>
        </div>
      </Background>
    </div>
  );
}

function MilestoneRow({ milestone }: { milestone: StreakMilestone }) {
  const translations = useAppSelector(selectTranslations);
  const lang = useAppSelector(selectLanguage);

  const statusLabel = {
    achieved: checkTranslationKey(translations[lang]?.Achieved, "Achieved"),
    next_up: checkTranslationKey(translations[lang]?.Next_up, "Next up"),
    locked: checkTranslationKey(translations[lang]?.Locked, "Locked"),
  }[milestone.status];

  return (
    <>
      <div className="milestone-day">
        <div className="pill">
          <span>
            {checkTranslationKey(translations[lang]?.Day, "Day")}
            {milestone.day}
          </span>
        </div>
        <div className="status">{statusLabel}</div>
      </div>

      <div className={`milestone-icon icon-${milestone.status}`}>
        <MilestoneIcon milestone={milestone} />
      </div>

      <div className="milestone-reward">
        <div className="pill">
          <span>
            {checkTranslationKey(translations[lang]?.Reward, "Reward")}
          </span>
        </div>
        <div className="reward-values">
          <span>{milestone.diamonds}</span>
          <img src="/img/icns/diamonds.png" alt="" />
          {milestone.coins > 0 && (
            <>
              <span className="plus">+</span>
              <span>{milestone.coins}</span>
              <img src="/img/icns/coins.png" alt="" />
            </>
          )}
        </div>
      </div>
    </>
  );
}

function MilestoneIcon({ milestone }: { milestone: StreakMilestone }) {
  // FR-30: the final milestone swaps its flame for the Iron Habit badge once earned
  if (milestone.achievement && milestone.status === "achieved") {
    return <img className="badge" src="/img/streak/iron-habit.png" alt="" />;
  }

  if (milestone.status === "achieved") {
    return <img className="flame" src="/img/streak/flame-lit.png" alt="" />;
  }

  if (milestone.status === "next_up") {
    return <img className="flame" src="/img/streak/flame-today.svg" alt="" />;
  }

  return <img className="lock" src="/img/streak/lock.svg" alt="" />;
}

export default StreakPage;
